import { useState } from 'react';
import { useMockAuth } from '@/hooks/useMockAuth';
import { useTheme } from '@/contexts/ThemeContext';

const ProfilePage = () => {
  const { user: currentUser } = useMockAuth();
  const { theme } = useTheme();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [productUpdates, setProductUpdates] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      inputBg: 'rgba(15, 15, 20, 0.6)',
      hover: '#2d2d44'
    },
    light: {
      bg: '#f9fafb',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      inputBg: '#f9fafb',
      hover: '#f3f4f6'
    }
  };

  const c = colors[theme];

  if (!currentUser) {
    return (
      <div style={{
        minHeight: '89.7vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: c.bg
      }}>
        <p style={{ color: c.textMuted, fontSize: '1.125rem' }}>
          Please log in to view your profile
        </p>
      </div>
    );
  }

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return '#dc2626';
      case 'creator':
        return '#7c3aed';
      case 'user':
        return '#2563eb';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div style={{ backgroundColor: c.bg, minHeight: '89.7vh' }}>
      {/* Hero Header */}
      <div style={{
        background: theme === 'dark'
          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '4rem 2rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem',
            flexWrap: 'wrap'
          }}>
            {/* Avatar */}
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              fontWeight: 'bold',
              color: getRoleColor(currentUser.role),
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              border: '4px solid rgba(255,255,255,0.3)'
            }}>
              {getInitials(currentUser.email)}
            </div>

            {/* User Info */}
            <div style={{ flex: 1, minWidth: '300px' }}>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '0.5rem',
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: '-0.02em'
              }}>
                {currentUser.email}
              </h1>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '0.375rem 1rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  backgroundColor: getRoleColor(currentUser.role),
                  color: 'white',
                  textTransform: 'uppercase'
                }}>
                  {currentUser.role}
                </span>
                <span style={{
                  padding: '0.375rem 1rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  backgroundColor: currentUser.status === 'active' ? '#16a34a' : '#dc2626',
                  color: 'white',
                  textTransform: 'capitalize'
                }}>
                  {currentUser.status}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              <StatBadge label="Member Since" value={formatDate(currentUser.createdAt)} />
              <StatBadge label="Purchases" value="12" />
              {currentUser.role === 'creator' && <StatBadge label="Products" value="5" />}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: '2rem'
        }}>
          {/* Profile Information */}
          <Section title="Profile Information" icon="👤" theme={theme}>
            <InfoRow label="Email" value={currentUser.email} theme={theme} />
            <InfoRow label="User ID" value={currentUser.userId} theme={theme} />
            <InfoRow label="Role" value={currentUser.role} theme={theme} />
            <InfoRow label="Status" value={currentUser.status} theme={theme} />
            <InfoRow label="Member Since" value={formatDate(currentUser.createdAt)} theme={theme} />
            <InfoRow label="Last Login" value={formatDate(currentUser.lastLoginAt)} theme={theme} />
          </Section>

          {/* Preferences */}
          <Section title="Notification Preferences" icon="🔔" theme={theme}>
            <ToggleRow
              label="Email Notifications"
              description="Receive updates about your account and purchases"
              checked={emailNotifications}
              onChange={setEmailNotifications}
              theme={theme}
            />
            <ToggleRow
              label="Product Updates"
              description="Get notified when products you own are updated"
              checked={productUpdates}
              onChange={setProductUpdates}
              theme={theme}
            />
            <ToggleRow
              label="Marketing Emails"
              description="Receive promotional offers and marketplace highlights"
              checked={marketingEmails}
              onChange={setMarketingEmails}
              theme={theme}
            />
          </Section>

          {/* Security */}
          <Section title="Security" icon="🔒" theme={theme}>
            <ActionButton
              icon="🔑"
              label="Change Password"
              description="Update your account password"
              onClick={() => alert('Password change functionality coming soon')}
              theme={theme}
            />
            <ActionButton
              icon="📱"
              label="Two-Factor Authentication"
              description="Add an extra layer of security"
              onClick={() => alert('2FA setup coming soon')}
              theme={theme}
            />
            <ActionButton
              icon="🔓"
              label="Active Sessions"
              description="View and manage your active sessions"
              onClick={() => alert('Session management coming soon')}
              theme={theme}
            />
          </Section>

          {/* Danger Zone */}
          <Section title="Danger Zone" icon="⚠️" danger theme={theme}>
            <div style={{
              padding: '1.5rem',
              backgroundColor: theme === 'dark' ? 'rgba(220, 38, 38, 0.1)' : '#fef2f2',
              border: `2px solid ${theme === 'dark' ? 'rgba(220, 38, 38, 0.3)' : '#fecaca'}`,
              borderRadius: '0.5rem'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#dc2626',
                marginBottom: '0.5rem'
              }}>
                Delete Account
              </h4>
              <p style={{
                fontSize: '0.875rem',
                color: theme === 'dark' ? '#fca5a5' : '#991b1b',
                marginBottom: '1rem'
              }}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                    alert('Account deletion functionality coming soon');
                  }
                }}
                style={{
                  padding: '0.625rem 1.25rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              >
                Delete Account
              </button>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

interface StatBadgeProps {
  label: string;
  value: string;
}

function StatBadge({ label, value }: StatBadgeProps) {
  return (
    <div style={{
      padding: '1rem',
      borderRadius: '0.75rem',
      background: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      minWidth: '120px'
    }}>
      <p style={{
        fontSize: '0.75rem',
        color: 'rgba(255, 255, 255, 0.9)',
        marginBottom: '0.25rem',
        fontWeight: '500'
      }}>
        {label}
      </p>
      <p style={{
        fontSize: '1.125rem',
        color: 'white',
        fontWeight: '600'
      }}>
        {value}
      </p>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  danger?: boolean;
  theme: 'dark' | 'light';
}

function Section({ title, icon, children, danger, theme }: SectionProps) {
  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      borderRadius: '1rem',
      padding: '2rem',
      border: danger ? '1px solid #fecaca' : `1px solid ${c.cardBorder}`,
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
      boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          color: danger ? '#dc2626' : c.text
        }}>
          {title}
        </h2>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        {children}
      </div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
  theme: 'dark' | 'light';
}

function InfoRow({ label, value, theme }: InfoRowProps) {
  // Use monospace font for technical identifiers
  const isTechnical = label === 'Email' || label === 'User ID';

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      backgroundColor: theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#f9fafb',
      borderRadius: '0.5rem'
    }}>
      <span style={{
        fontSize: '0.875rem',
        color: theme === 'dark' ? '#9ca3af' : '#6b7280',
        fontWeight: '500'
      }}>
        {label}
      </span>
      <span style={{
        fontSize: '0.875rem',
        color: theme === 'dark' ? '#ffffff' : '#111827',
        fontWeight: '600',
        fontFamily: isTechnical ? "'JetBrains Mono', monospace" : 'inherit',
        letterSpacing: isTechnical ? '-0.02em' : '0'
      }}>
        {value}
      </span>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  theme: 'dark' | 'light';
}

function ToggleRow({ label, description, checked, onChange, theme }: ToggleRowProps) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      backgroundColor: theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#f9fafb',
      borderRadius: '0.5rem'
    }}>
      <div style={{ flex: 1 }}>
        <p style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: theme === 'dark' ? '#ffffff' : '#111827',
          marginBottom: '0.25rem'
        }}>
          {label}
        </p>
        <p style={{
          fontSize: '0.75rem',
          color: theme === 'dark' ? '#9ca3af' : '#6b7280'
        }}>
          {description}
        </p>
      </div>
      <label style={{
        position: 'relative',
        display: 'inline-block',
        width: '48px',
        height: '24px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{
            opacity: 0,
            width: 0,
            height: 0
          }}
        />
        <span style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: checked ? '#667eea' : '#d1d5db',
          borderRadius: '24px',
          transition: 'background-color 0.2s',
          cursor: 'pointer'
        }}>
          <span style={{
            position: 'absolute',
            height: '18px',
            width: '18px',
            left: checked ? '27px' : '3px',
            bottom: '3px',
            backgroundColor: 'white',
            borderRadius: '50%',
            transition: 'left 0.2s'
          }} />
        </span>
      </label>
    </div>
  );
}

interface ActionButtonProps {
  icon: string;
  label: string;
  description: string;
  onClick: () => void;
  theme: 'dark' | 'light';
}

function ActionButton({ icon, label, description, onClick, theme }: ActionButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  const hoverBg = theme === 'dark' ? 'rgba(102, 126, 234, 0.1)' : '#f3f4f6';
  const normalBg = theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#f9fafb';

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
        backgroundColor: isHovered ? hoverBg : normalBg,
        border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb'}`,
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'left',
        width: '100%'
      }}
    >
      <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <p style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: theme === 'dark' ? '#ffffff' : '#111827',
          marginBottom: '0.25rem'
        }}>
          {label}
        </p>
        <p style={{
          fontSize: '0.75rem',
          color: theme === 'dark' ? '#9ca3af' : '#6b7280'
        }}>
          {description}
        </p>
      </div>
      <span style={{
        fontSize: '1.25rem',
        color: theme === 'dark' ? '#9ca3af' : '#9ca3af',
        transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
        transition: 'transform 0.2s'
      }}>
        →
      </span>
    </button>
  );
}

export default ProfilePage;
